import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS as cssDndKit } from '@dnd-kit/utilities';

import type { ApiFilestackImageOrVideoDataWithCaption } from '#/api/types/ApiFilestackImageData';
import { Button } from '#/components/Button/Button';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { Icon } from '#/components/Icon/Icon';
import { VideoThumbnail } from '#/components/VideoThumbnail/VideoThumbnail';
import { useI18n } from '#/i18n/hooks/useI18n';

import { DeleteButton } from './DeleteButton';
import { EditOverlay } from './EditOverlay';
import { ImageContainer } from './ImageContainer';

type Props = {
  id: UniqueIdentifier;
  activeId: UniqueIdentifier | null;
  image: ApiFilestackImageOrVideoDataWithCaption;
  removeImage: () => void;
  editImage: () => void;
  qaId: string;
};

export function SortableImage({
  id,
  activeId,
  image,
  removeImage,
  editImage,
  qaId,
}: Props) {
  const { i18n } = useI18n();

  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({
      id,
    });

  const style = {
    transform: cssDndKit.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef}>
      <ImageContainer
        data-qa-id={`${qaId}-uploaded-image`}
        style={style}
        className={
          activeId === id ? 'sortable-item dragging-dnd-kit' : 'sortable-item'
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...attributes}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...listeners}
      >
        {image.type === 'IMAGE' && (
          <FilestackImage data={image} expandSize objectFit="cover" />
        )}

        {image.type === 'VIDEO' && (
          <VideoThumbnail
            src={{ type: 'video-thumbnail', videoUrl: image.videoUrl }}
            size={{ type: 'expand' }}
            playIcon={{ size: 'small' }}
          />
        )}
        <EditOverlay>
          <Button
            type="button"
            variant="primary"
            qaId={`${qaId}-edit-image`}
            onClick={editImage}
            size="small"
          >
            {i18n('Edit')}
          </Button>
        </EditOverlay>
        <DeleteButton
          title={i18n('Remove')}
          data-qa-id={`${qaId}-remove-image`}
          onClick={(e) => {
            e.preventDefault();
            removeImage();
          }}
        >
          <Icon name="trash" size={14} />
        </DeleteButton>
      </ImageContainer>
    </div>
  );
}
